import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
  Tag, Plus, Trash2, Loader2, Pencil, Percent, DollarSign,
  Package, Sparkles, Info, Clock, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OffersPage() {
  const { authHeaders, activeBranch } = useAuth();
  const { t } = useTranslation();
  const [offers, setOffers] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formItemId, setFormItemId] = useState('');
  const [formDiscount, setFormDiscount] = useState(15);
  const [formOfferPrice, setFormOfferPrice] = useState(0);
  const [formBadge, setFormBadge] = useState('عرض خاص');
  const [formDesc, setFormDesc] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [formScheduleType, setFormScheduleType] = useState('always');
  const [formScheduleDays, setFormScheduleDays] = useState([]);
  const [formStartHour, setFormStartHour] = useState(0);
  const [formEndHour, setFormEndHour] = useState(24);

  useEffect(() => { if (activeBranch) fetchAll(); }, [activeBranch]);

  const fetchAll = async () => {
    if (!activeBranch) return;
    setLoading(true);
    try {
      const [offersR, itemsR] = await Promise.all([
        axios.get(`${API}/api/offers?branch_id=${activeBranch.id}`, authHeaders()),
        axios.get(`${API}/api/offers/menu-items?branch_id=${activeBranch.id}`, authHeaders()),
      ]);
      setOffers(offersR.data);
      setMenuItems(itemsR.data);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setFormItemId('');
    setFormDiscount(15);
    setFormOfferPrice(0);
    setFormBadge('عرض خاص');
    setFormDesc('');
    setFormActive(true);
    setFormScheduleType('always');
    setFormScheduleDays([]);
    setFormStartHour(0);
    setFormEndHour(24);
    setDialogOpen(true);
  };

  const openEdit = (offer) => {
    setEditing(offer);
    setFormItemId(offer.item_id);
    setFormDiscount(offer.discount_pct || 0);
    setFormOfferPrice(offer.offer_price || 0);
    setFormBadge(offer.badge || 'عرض خاص');
    setFormDesc(offer.description || '');
    setFormActive(offer.is_active !== false);
    const sch = offer.schedule || { type: 'always' };
    setFormScheduleType(sch.type || 'always');
    setFormScheduleDays(sch.days || []);
    setFormStartHour(sch.start_hour ?? 0);
    setFormEndHour(sch.end_hour ?? 24);
    setDialogOpen(true);
  };

  const buildSchedule = () => {
    if (formScheduleType === 'always') return { type: 'always' };
    if (formScheduleType === 'time_range') return { type: 'time_range', start_hour: formStartHour, end_hour: formEndHour };
    if (formScheduleType === 'days') return { type: 'days', days: formScheduleDays };
    if (formScheduleType === 'days_and_time') return { type: 'days_and_time', days: formScheduleDays, start_hour: formStartHour, end_hour: formEndHour };
    return { type: 'always' };
  };

  const handleSave = async () => {
    if (!formItemId && !editing) {
      toast.error(t('offers.selectItem'));
      return;
    }
    setSaving(true);
    const schedule = buildSchedule();
    try {
      if (editing) {
        await axios.put(`${API}/api/offers/${editing.id}`, {
          discount_pct: formDiscount,
          offer_price: formOfferPrice,
          badge: formBadge,
          description: formDesc,
          is_active: formActive,
          schedule,
        }, authHeaders());
      } else {
        await axios.post(`${API}/api/offers?branch_id=${activeBranch.id}`, {
          item_id: formItemId,
          discount_pct: formDiscount,
          offer_price: formOfferPrice,
          badge: formBadge,
          description: formDesc,
          is_active: formActive,
          schedule,
        }, authHeaders());
      }
      toast.success(t('offers.saved'));
      setDialogOpen(false);
      fetchAll();
    } catch {
      toast.error(t('offers.failed'));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('offers.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/api/offers/${id}`, authHeaders());
      toast.success(t('offers.deleted'));
      setOffers(prev => prev.filter(o => o.id !== id));
    } catch {
      toast.error(t('offers.failed'));
    }
  };

  const handleToggle = async (offer) => {
    try {
      await axios.put(`${API}/api/offers/${offer.id}`, { is_active: !offer.is_active }, authHeaders());
      setOffers(prev => prev.map(o => o.id === offer.id ? { ...o, is_active: !o.is_active } : o));
    } catch {
      toast.error(t('offers.failed'));
    }
  };

  const selectedItem = menuItems.find(m => m.id === formItemId);
  const calcOfferPrice = selectedItem ? Math.round(selectedItem.price * (1 - formDiscount / 100)) : 0;
  const activeCount = offers.filter(o => o.is_active).length;
  const badgePresets = t('offers.badgePresets', { returnObjects: true }) || [];

  if (!activeBranch) return (
    <ModuleLayout moduleKey="operations">
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-muted-foreground">
        <Tag className="w-12 h-12 mb-4 opacity-30" />
        <p className="text-sm">{t('common.selectBranch')}</p>
      </div>
    </ModuleLayout>
  );

  if (loading) return (
    <ModuleLayout moduleKey="operations">
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    </ModuleLayout>
  );

  return (
    <ModuleLayout moduleKey="operations">
      <div className="space-y-5" data-testid="offers-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-bold text-xl md:text-2xl text-foreground flex items-center gap-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Tag className="w-5 h-5 text-primary" />
              </div>
              {t('offers.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">{t('offers.subtitle')}</p>
          </div>
          <Button onClick={openCreate} className="rounded-full gap-1.5" data-testid="add-offer-btn">
            <Plus className="w-4 h-4" /> {t('offers.addOffer')}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-xl bg-primary/10"><Tag className="w-4 h-4 text-primary" /></div>
              <div>
                <p className="text-xl font-bold">{offers.length}</p>
                <p className="text-[11px] text-muted-foreground">{t('offers.totalOffers')}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-xl bg-emerald-500/10"><Sparkles className="w-4 h-4 text-emerald-500" /></div>
              <div>
                <p className="text-xl font-bold">{activeCount}</p>
                <p className="text-[11px] text-muted-foreground">{t('offers.activeOffers')}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Banner */}
        {offers.length === 0 && (
          <Card className="border border-primary/20 bg-primary/5">
            <CardContent className="p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-foreground">{t('offers.autoGenerated')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('offers.autoDesc')}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Offers List */}
        {offers.length === 0 ? (
          <Card className="border border-dashed">
            <CardContent className="p-8 text-center">
              <Tag className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-sm text-muted-foreground">{t('offers.noOffers')}</p>
              <Button onClick={openCreate} variant="outline" className="mt-4 rounded-full gap-1" data-testid="add-offer-empty">
                <Plus className="w-3.5 h-3.5" /> {t('offers.addOffer')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {offers.map(offer => (
              <Card key={offer.id} className={`border shadow-sm transition-all ${offer.is_active ? '' : 'opacity-50'}`} data-testid={`offer-${offer.id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    {offer.image_url ? (
                      <img src={offer.image_url} alt="" className="w-full sm:w-14 h-32 sm:h-14 rounded-xl object-cover flex-shrink-0" />
                    ) : (
                      <div className="hidden sm:flex w-14 h-14 rounded-xl bg-muted items-center justify-center flex-shrink-0">
                        <Package className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold truncate">{offer.item_name || offer.name}</span>
                        <Badge className="text-[10px] bg-primary/10 text-primary border-0">{offer.badge}</Badge>
                        {offer.schedule?.type && offer.schedule.type !== 'always' && (
                          <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-600 border-blue-500/20 gap-0.5">
                            <Clock className="w-2.5 h-2.5" /> {t('offers.scheduledLabel')}
                          </Badge>
                        )}
                        {offer.is_active ? (
                          <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/20">{t('offers.active')}</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-600 border-red-500/20">{t('offers.inactive')}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="font-bold text-primary">{offer.offer_price} {t('common.sar')}</span>
                        <span className="text-muted-foreground line-through text-xs">{offer.original_price} {t('common.sar')}</span>
                        <Badge variant="secondary" className="text-[10px]">-{offer.discount_pct}%</Badge>
                      </div>
                      {offer.description && <p className="text-xs text-muted-foreground mt-1 truncate">{offer.description}</p>}
                      {offer.schedule?.type && offer.schedule.type !== 'always' && (
                        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-blue-500">
                          <Clock className="w-3 h-3" />
                          {offer.schedule.type === 'time_range' && <span>{offer.schedule.start_hour}:00 - {offer.schedule.end_hour}:00</span>}
                          {offer.schedule.type === 'days' && <span>{(offer.schedule.days || []).map(d => t(`offers.${d}`)).join(', ')}</span>}
                          {offer.schedule.type === 'days_and_time' && <span>{(offer.schedule.days || []).map(d => t(`offers.${d}`)).join(', ')} | {offer.schedule.start_hour}:00 - {offer.schedule.end_hour}:00</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-center">
                      <Switch checked={offer.is_active} onCheckedChange={() => handleToggle(offer)} data-testid={`toggle-${offer.id}`} />
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(offer)} data-testid={`edit-${offer.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" onClick={() => handleDelete(offer.id)} data-testid={`delete-${offer.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-[calc(100vw-32px)] sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="offer-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? t('offers.editOffer') : t('offers.addOffer')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Item Select */}
              {!editing && (
                <div>
                  <Label className="text-xs mb-1.5 block">{t('offers.selectItem')}</Label>
                  <Select value={formItemId} onValueChange={setFormItemId}>
                    <SelectTrigger data-testid="offer-item-select"><SelectValue placeholder={t('offers.selectItem')} /></SelectTrigger>
                    <SelectContent>
                      {menuItems.map(item => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name} - {item.price} {t('common.sar')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Discount */}
              <div>
                <Label className="text-xs mb-1.5 block flex items-center gap-1">
                  <Percent className="w-3 h-3" /> {t('offers.discountPct')}
                </Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min={0} max={90} value={formDiscount} onChange={e => {
                    const val = parseInt(e.target.value) || 0;
                    setFormDiscount(val);
                    if (selectedItem) setFormOfferPrice(Math.round(selectedItem.price * (1 - val / 100)));
                  }} className="h-9 text-sm w-24" data-testid="offer-discount" />
                  <span className="text-xs text-muted-foreground">%</span>
                  {selectedItem && (
                    <span className="text-xs text-muted-foreground">
                      {t('offers.originalPrice')}: {selectedItem.price} {t('common.sar')} → <strong className="text-primary">{calcOfferPrice} {t('common.sar')}</strong>
                    </span>
                  )}
                </div>
              </div>

              {/* Offer Price Override */}
              <div>
                <Label className="text-xs mb-1.5 block flex items-center gap-1">
                  <DollarSign className="w-3 h-3" /> {t('offers.offerPrice')}
                </Label>
                <Input type="number" min={0} value={formOfferPrice || calcOfferPrice} onChange={e => setFormOfferPrice(parseInt(e.target.value) || 0)}
                  className="h-9 text-sm w-32" data-testid="offer-price" />
              </div>

              {/* Badge */}
              <div>
                <Label className="text-xs mb-1.5 block">{t('offers.badge')}</Label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(Array.isArray(badgePresets) ? badgePresets : []).map((b, i) => (
                    <button key={i} onClick={() => setFormBadge(b)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${formBadge === b ? 'bg-primary/10 text-primary border-primary/30' : 'bg-muted text-muted-foreground border-transparent hover:border-border'}`}>
                      {b}
                    </button>
                  ))}
                </div>
                <Input value={formBadge} onChange={e => setFormBadge(e.target.value)} className="h-9 text-sm" data-testid="offer-badge" />
              </div>

              {/* Description */}
              <div>
                <Label className="text-xs mb-1.5 block">{t('offers.description')}</Label>
                <Input value={formDesc} onChange={e => setFormDesc(e.target.value)} placeholder={t('offers.description')} className="h-9 text-sm" data-testid="offer-desc" />
              </div>

              {/* Schedule */}
              <div className="space-y-3 p-3 rounded-xl bg-muted/30 border border-border/50" data-testid="schedule-section">
                <Label className="text-xs font-semibold flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-blue-500" /> {t('offers.scheduleType')}
                </Label>
                <Select value={formScheduleType} onValueChange={setFormScheduleType}>
                  <SelectTrigger className="h-8 text-xs" data-testid="schedule-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="always">{t('offers.always')}</SelectItem>
                    <SelectItem value="time_range">{t('offers.timeRange')}</SelectItem>
                    <SelectItem value="days">{t('offers.specificDays')}</SelectItem>
                    <SelectItem value="days_and_time">{t('offers.daysAndTime')}</SelectItem>
                  </SelectContent>
                </Select>

                {/* Time Range */}
                {(formScheduleType === 'time_range' || formScheduleType === 'days_and_time') && (
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground mb-1 block">{t('offers.startHour')}</Label>
                      <Select value={String(formStartHour)} onValueChange={v => setFormStartHour(parseInt(v))}>
                        <SelectTrigger className="h-8 text-xs" data-testid="schedule-start"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => (
                            <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <span className="text-muted-foreground text-xs mt-4">-</span>
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground mb-1 block">{t('offers.endHour')}</Label>
                      <Select value={String(formEndHour)} onValueChange={v => setFormEndHour(parseInt(v))}>
                        <SelectTrigger className="h-8 text-xs" data-testid="schedule-end"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => i + 1).map(h => (
                            <SelectItem key={h} value={String(h === 24 ? 24 : h)}>{String(h === 24 ? '00' : h).padStart(2, '0')}:00</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Days Selection */}
                {(formScheduleType === 'days' || formScheduleType === 'days_and_time') && (
                  <div>
                    <Label className="text-[10px] text-muted-foreground mb-1.5 block">{t('offers.selectDays')}</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map(day => (
                        <button key={day} onClick={() => setFormScheduleDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])}
                          className={`px-2.5 py-1.5 rounded-lg text-[10px] font-semibold border transition-all ${formScheduleDays.includes(day) ? 'bg-blue-500/15 text-blue-600 border-blue-500/30' : 'bg-muted/50 text-muted-foreground border-transparent hover:border-border'}`}
                          data-testid={`schedule-day-${day}`}>
                          {t(`offers.${day}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick presets */}
                {formScheduleType === 'always' && (
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => { setFormScheduleType('time_range'); setFormStartHour(12); setFormEndHour(16); setFormBadge(t('offers.lunchOffer')); }}
                      className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-blue-500/10 text-blue-500 border border-blue-500/20 hover:bg-blue-500/20 transition-all">
                      {t('offers.lunchOffer')} (12-16)
                    </button>
                    <button onClick={() => { setFormScheduleType('time_range'); setFormStartHour(18); setFormEndHour(23); setFormBadge(t('offers.eveningOffer')); }}
                      className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-purple-500/10 text-purple-500 border border-purple-500/20 hover:bg-purple-500/20 transition-all">
                      {t('offers.eveningOffer')} (18-23)
                    </button>
                    <button onClick={() => { setFormScheduleType('days'); setFormScheduleDays(['fri', 'sat']); setFormBadge(t('offers.weekendOffer')); }}
                      className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all">
                      {t('offers.weekendOffer')}
                    </button>
                  </div>
                )}

                {formScheduleType !== 'always' && (
                  <p className="text-[10px] text-blue-500 flex items-center gap-1"><Info className="w-3 h-3" /> {t('offers.scheduleInfo')}</p>
                )}
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between">
                <Label className="text-xs">{t('offers.active')}</Label>
                <Switch checked={formActive} onCheckedChange={setFormActive} data-testid="offer-active-toggle" />
              </div>

              <Button onClick={handleSave} disabled={saving || (!editing && !formItemId)} className="w-full rounded-full" data-testid="offer-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {editing ? t('common.save') : t('offers.addOffer')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ModuleLayout>
  );
}
